import React from 'react'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { Spin, Table, Radio, Tooltip } from 'antd'
import { layerManage } from '@/utils/layer'
import { Row } from '@/components/layout/layout'
import Iconfont from '@/components/iconfont/iconfont'
import Button from '@/components/button/button'
import { RefundMallGoodsList } from './order-refund'
import { ColumnsType } from 'antd/lib/table'
import { errorCallBack } from '@/utils'
import $msg from '@/utils/message'
import { $post } from '@/api'
import { useRequest } from 'ahooks'
import Big from 'big.js'
import useBemClass from '@/utils/hooks/useBEMClass'
import { useRefund } from '../../order-cashier/order-refund/order-refund-confirm'
import { IMallOrderDetail } from '../order-list/order-detail/type'
import './order-refund.scss'
import { mallOrderStore } from '../store'

interface OrderRefundConfirmProps {
    refundGoods: RefundMallGoodsList[]
    payments: IMallOrderDetail.Payment[]
    remark: string
    subtotal: number
    orderId: number
    orderType: number
    deliveryFee?: number
}

const MallOrderRefundConfirm: React.FC<OrderRefundConfirmProps> = props => {
    const cls = useBemClass('cashier-order-refund')

    const { selectType, setSelectType, paymentType, customPaymentData } = useRefund(props.payments)

    const refundReq = useRequest(
        () =>
            $post({
                url: '/mall/onlineorderrefund',
                params: {
                    order_id: props.orderId,
                    order_type: props.orderType,
                    refund_subtotal: +Big(props.subtotal).plus(props.deliveryFee || 0),
                    refund_delivery_fee: !!props.deliveryFee,
                    refund_goods: props.refundGoods
                        .filter(g => g.return_stock !== 2)
                        .map(g => ({
                            order_goods_id: g.order_goods_id,
                            refund_amount: g.refund_amount,
                            refund_subtotal: g.refund_subtotal,
                            return_stock: !!g.return_stock,
                        })),
                    refund_type: selectType.toString().startsWith('6') ? 6 : selectType,
                    refund_mark: props.remark,
                    custom_payment_id: selectType.toString().startsWith('6')
                        ? +selectType.toString().substring(1)
                        : undefined,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '退款成功')
                layerManage.pop()
                mallOrderStore.setOrderListRefresh()
            },
            onError: errorCallBack,
        },
    )

    const columns: ColumnsType<RefundMallGoodsList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'goods',
            width: 500,
            ellipsis: true,
            render: (v, r) => r.goods_name,
        },
        {
            title: '退货量',
            width: 70,
            render: (v, r) => (r.return_stock ? r.refund_amount : '-'),
        },
        {
            title: '库存',
            render: (v, r) => (r.return_stock ? `+${r.refund_amount}` : '-'),
        },
        {
            title: '退款',
            render: (v, r) => '¥' + r.refund_subtotal,
        },
    ]

    return (
        <BaseDialog isShow title="退款确认" onClose={() => layerManage.pop()}>
            <Spin spinning={customPaymentData.loading}>
                <div className={cls('tip')}>*仅支持退款一次，请仔细确认退款信息</div>
                <div className={cls('confirm-table-container')}>
                    <Table
                        columns={columns}
                        rowKey={r => r.order_goods_id}
                        dataSource={props.refundGoods}
                        pagination={false}
                        bordered
                        scroll={{ x: 'max-content', y: 225 }}
                    ></Table>
                    <div className={cls('confirm-bottom')}>
                        <Row>
                            <span data-color-333 data-font-size-16>
                                备注：
                            </span>
                            <div className={cls('confirm-remark')}>{props.remark || '无'}</div>
                        </Row>
                        <Row data-align-items-flex-end>
                            <span data-font-size-14>总退款：</span>
                            <span data-font-size-12 data-color-ff4343 data-font-weight-600>
                                ¥
                            </span>
                            <span
                                style={{
                                    lineHeight: '24px',
                                }}
                                data-font-size-24
                                data-color-price
                                data-font-weight-600
                            >
                                {props.deliveryFee ? props.subtotal + (props.deliveryFee || 0) : props.subtotal}
                            </span>
                            {!!props.deliveryFee && (
                                <span>
                                    (含配送费<span data-color-price>¥{props.deliveryFee}</span>元)
                                </span>
                            )}
                        </Row>
                    </div>
                </div>
                <Row data-center data-aling-items-center>
                    <div className={cls('line')} />
                    <div data-color-999 data-font-size-16 data-margin-h-8 data-margin-v-15>
                        退款方式
                    </div>
                    <div className={cls('line')} />
                </Row>
                <Radio.Group
                    onChange={e => {
                        setSelectType(e.target.value)
                    }}
                    value={selectType}
                >
                    <Row className={cls('group')} data-align-items-center data-padding-left-16>
                        {paymentType.map(p => (
                            <div key={p.id} className={cls('radio-container')} data-align-items-center>
                                <Radio value={p.id}>{p.name}</Radio>
                                {p.des && (
                                    <Tooltip placement="topLeft" trigger={['click', 'hover']} title={p.des}>
                                        <Iconfont
                                            name="icon8yiwen-16"
                                            size={16}
                                            style={{
                                                marginLeft: '-6px',
                                                zIndex: 10,
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        ))}
                    </Row>
                </Radio.Group>
                <Row className={cls('comfirm-btn')}>
                    <Button
                        types="text"
                        style={{
                            marginRight: '16px',
                            color: '#999',
                        }}
                    >
                        取消
                    </Button>
                    <Button
                        disabled={!props.subtotal || selectType === -1}
                        types="danger"
                        loading={refundReq.loading}
                        onClick={refundReq.run}
                    >
                        确认退款
                    </Button>
                </Row>
            </Spin>
        </BaseDialog>
    )
}

export default MallOrderRefundConfirm
